import {
    Component,
    OnInit,
    Inject,
    ChangeDetectorRef,
    ElementRef,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Solicitud } from '../../../../../interfaces/interface-bakend/ofertas-laborales/solicitudes.interface';
import { MediaService } from '../../../../../services/shared/media.service';
import { SolicitudesService } from '../../../../../services/ofertas-laborales/solicitudes.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import {
    MAX_ARCHIVO_MB,
    PLACEHOLDER_HEIC,
    esHeic,
} from '../../../common/helpers/archivos.helper';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';

export interface DialogData {
    solicitud: Solicitud;
    adjunto: string;
    label: string;
    numberState: number;
    labelButton: string;
    accept: string;
}

@Component({
    selector: 'app-agregar-documentos-extra',
    templateUrl: './agregar-documentos-extra.component.html',
    styleUrls: ['./agregar-documentos-extra.component.scss'],
})
export class AgregarDocumentosExtraComponent implements OnInit, OnDestroy {
    maxArchivoMb = MAX_ARCHIVO_MB;

    value = 0;
    subscriptionAvance: Subscription;
    flagLoading = false;
    selectedFile: File | null = null;
    previewUrl: SafeUrl | null = null;
    flagDisableButton = true;

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    constructor(
        private mediaService: MediaService,
        private solicitudesService: SolicitudesService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
        private modalCustom: PopUpModalCustomService,
        public dialogRef: MatDialogRef<AgregarDocumentosExtraComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogData
    ) {
        this.mediaService.reiniciarContadorControlUpload();
        this.subscribeAvanceUpload();
    }

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    ngOnInit(): void {}

    // =====================================================
    // Selección de archivo
    // =====================================================

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files[0]) {
            return;
        }

        const file = input.files[0];

        if (!this.mediaService.controlSize(event)) {
            input.value = '';
            this.modalCustom.abrirPopup({
                tipo: 'warning',
                titulo: 'Archivo demasiado grande',
                mensaje: `El archivo excede el tamaño máximo permitido de ${this.maxArchivoMb} MB.`,
            });
            return;
        }

        // Chrome/Edge no decodifican HEIC/HEIF: se sube crudo (el backend lo
        // convierte a WebP) y el preview muestra un placeholder.
        if (esHeic(file)) {
            this.selectedFile = file;
            this.flagDisableButton = false;
            this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(PLACEHOLDER_HEIC);
            this.cdr.detectChanges();
            return;
        }

        if (file.type.startsWith('image/')) {
            this.resizeImage(file, 1024, 1024, (resizedBlob) => {
                if (!resizedBlob) {
                    return;
                }
                this.selectedFile = new File([resizedBlob], file.name, {
                    type: file.type,
                });
                this.flagDisableButton = false;
                this.previewFile(this.selectedFile);
            });
            return;
        }

        this.selectedFile = file;
        this.flagDisableButton = false;
        this.previewFile(file);
    }

    previewFile(file: File): void {
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            if (e.target?.result) {
                this.previewUrl = this.sanitizer.bypassSecurityTrustUrl(
                    e.target.result as string
                );
                this.cdr.detectChanges();
            }
        };
        reader.readAsDataURL(file);
    }

    clearSelectedFile(): void {
        this.selectedFile = null;
        this.previewUrl = null;
        this.flagDisableButton = true;
        this.value = 0;

        if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }

        this.cdr.detectChanges();
    }

    // =====================================================
    // Upload
    // =====================================================

    subscribeAvanceUpload(): void {
        this.subscriptionAvance = this.mediaService.getAvance().subscribe((resp) => {
            this.value = resp ? resp.value : 0;
        });
    }

    async uploadDocumentoAdjunto(selectedFile: File): Promise<boolean> {
        let idUsuario;
        if (this.data.solicitud.usuario.hasOwnProperty('_id')) {
            idUsuario = this.data.solicitud.usuario._id;
        } else {
            idUsuario = this.data.solicitud.usuario;
        }

        const resp = await this.mediaService.uploadOneAttachJobOfferFromWeb(
            selectedFile,
            idUsuario,
            this.data.adjunto,
            this.data.solicitud._id,
            false
        );

        if (resp && resp['ok']) {
            this.solicitudesService.setSolicitudSeleccionada(resp['solicitud']);
            return true;
        }
        return false;
    }

    async aceptar(): Promise<void> {
        if (this.flagLoading || !this.selectedFile) {
            return;
        }

        this.flagDisableButton = true;
        this.flagLoading = true;
        this.mediaService.incrementarContadorControlUpload();

        if (!this.mediaService.controlContador()) {
            this.flagLoading = false;
            return;
        }

        try {
            const ok = await this.uploadDocumentoAdjunto(this.selectedFile);
            if (ok) {
                this.dialogRef.close({
                    ok: true,
                    fileName: this.selectedFile?.name ?? null,
                    file: this.selectedFile,
                });
                return;
            }
            this.modalCustom.abrirPopup({
                tipo: 'error',
                titulo: 'No se pudo subir el archivo',
                mensaje:
                    'Ocurrió un error al subir el documento. Verificá tu conexión e intentá nuevamente.',
            });
        } finally {
            this.flagLoading = false;
            this.mediaService.reiniciarContadorControlUpload();
        }
    }

    closeModal(): void {
        this.dialogRef.close(false);
    }

    // =====================================================
    // Resize de imágenes
    // =====================================================

    resizeImage(
        file: File,
        maxWidth: number,
        maxHeight: number,
        callback: (result: Blob) => void
    ): void {
        const outputType = file.type;
        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();
            img.src = (event.target as FileReader).result as string;

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    callback(file);
                    return;
                }

                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else if (height > maxHeight) {
                    width *= maxHeight / height;
                    height = maxHeight;
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            callback(blob);
                        } else {
                            callback(file);
                        }
                        img.src = '';
                        canvas.width = 0;
                        canvas.height = 0;
                    },
                    outputType,
                    0.8
                );
            };

            img.onerror = () => callback(file);
        };

        reader.onerror = () => callback(file);
        reader.readAsDataURL(file);
    }
}
